import React from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FaTimes } from 'react-icons/fa'

interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  className?: string
  children: React.ReactNode
  footer?: React.ReactNode
  width?: string
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  className = '',
  children,
  footer,
  width = "770px"
}: DrawerProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — portalled to body to escape any ancestor transform stacking context */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
            style={{ zIndex: 9998 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Drawer panel — slides in from the right, full viewport height */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ zIndex: 9999, width, maxWidth: '100vw', right: 0, maxHeight: 'calc(100vh)' }}
            className={`fixed top-0 right-0 h-screen bg-table-header-bg shadow-2xl border border-border-color flex flex-col overflow-hidden ${className}`}
          >
            {/* Close button row */}
            <div className="flex items-center justify-between px-6 py-3 shrink-0">
              <div>
                {title && <h2 className="text-xl font-semibold text-primary-black">{title}</h2>}
                {description && <p className="text-sm text-text-primary mt-1">{description}</p>}
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-full p-2 text-slate-600 hover:bg-slate-100 hover:text-primary-black"
                aria-label="Close"
              >
                <FaTimes size={18} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-4 pb-6">
              {children}
            </div>

            {/* Optional footer */}
            {footer && (
              <div className="shrink-0 border-t border-border-color bg-white px-6 py-4">
                {footer}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
